import React, { useState } from 'react';

export interface StripeCheckoutPlan {
  id: string;
  name: string;
  price: string;
  billingInterval: 'month' | 'year';
  description: string;
  includedSeats?: number;
  sponsoredPatientsLimit?: number;
}

interface StripeMockCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: StripeCheckoutPlan | null;
  onSuccess: (receipt: { id: string; planName: string; amount: string; date: string }) => void;
}

const STRIPE_PRICE_MAPPINGS: Record<string, { sandbox: string; live: string }> = {
  mindful_plus_trial: {
    sandbox: 'price_1UHuOuJM5ejvHenBuUF4yOVZ',
    live: 'price_1UHuOzJM5ejvHenBPmAhIErY',
  },
  solo_practitioner_plan: {
    sandbox: 'price_1UHuOuJM5ejvHenBTYFbSnhZ',
    live: 'price_1UHuOzJM5ejvHenBu9VpBQXG',
  },
  outpatient_clinic_plan: {
    sandbox: 'price_1UHuOvJM5ejvHenB1BYpPnoN',
    live: 'price_1UHuP0JM5ejvHenB7XQjBZld',
  },
  clinic_team_annual: {
    sandbox: 'price_1UHuOvJM5ejvHenB7ipSCePJ',
    live: 'price_1UHuP0JM5ejvHenBtgmAdhg2',
  },
  addon_extra_clinician_seat: {
    sandbox: 'price_1UHuOvJM5ejvHenB3Jy1DTiG',
    live: 'price_1UHuP0JM5ejvHenBpQpmv1OK',
  },
  addon_extra_patients_pack: {
    sandbox: 'price_1UHuOvJM5ejvHenBYpX8ccOH',
    live: 'price_1UHuP0JM5ejvHenBbrC3c4rZ',
  },
};

export const StripeMockCheckoutModal: React.FC<StripeMockCheckoutModalProps> = ({
  isOpen,
  onClose,
  plan,
  onSuccess,
}) => {
  const [stripeMode, setStripeMode] = useState<'sandbox' | 'live'>('sandbox');
  const [cardNumber, setCardNumber] = useState<string>('4242 •••• •••• 4242');
  const [expDate, setExpDate] = useState<string>('12/28');
  const [cvc, setCvc] = useState<string>('888');
  const [billingName, setBillingName] = useState<string>('Care Lead Practitioner');
  const [billingZip, setBillingZip] = useState<string>('94105');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [checkoutComplete, setCheckoutComplete] = useState<boolean>(false);
  const [completedReceipt, setCompletedReceipt] = useState<{ id: string; planName: string; amount: string; date: string } | null>(null);

  if (!isOpen || !plan) return null;

  const matchedPrice = plan.id && STRIPE_PRICE_MAPPINGS[plan.id]
    ? STRIPE_PRICE_MAPPINGS[plan.id][stripeMode]
    : 'price_custom_catalog';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
      const receipt = {
        id: `${stripeMode === 'live' ? 'ch_live' : 'ch_test'}_${Math.random().toString(36).substring(2, 10)}`,
        planName: plan.name,
        amount: plan.price,
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      };
      setCompletedReceipt(receipt);
      setCheckoutComplete(true);
      onSuccess(receipt);
    }, 900);
  };

  const handleCloseModal = () => {
    setCheckoutComplete(false);
    setCompletedReceipt(null);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          maxWidth: '540px',
          width: '100%',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
        }}
      >
        {/* Stripe Header Ribbon */}
        <div style={{
          backgroundColor: stripeMode === 'live' ? '#1B4332' : '#635BFF',
          color: '#FFFFFF',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.5px' }}>stripe</span>
            <span style={{
              backgroundColor: stripeMode === 'live' ? '#52B788' : 'rgba(255, 255, 255, 0.25)',
              color: stripeMode === 'live' ? '#081C15' : '#FFFFFF',
              fontSize: '10.5px',
              padding: '3px 8px',
              borderRadius: '999px',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              {stripeMode === 'live' ? '● Live Production (acct_1UHtamJM5ejvHenB)' : 'Test Sandbox Mode'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Mode Switcher Buttons */}
            <div style={{ display: 'inline-flex', backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px', borderRadius: '999px' }}>
              <button
                type="button"
                onClick={() => setStripeMode('sandbox')}
                style={{
                  background: stripeMode === 'sandbox' ? '#FFFFFF' : 'transparent',
                  color: stripeMode === 'sandbox' ? '#635BFF' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '999px',
                  padding: '3px 10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Sandbox
              </button>
              <button
                type="button"
                onClick={() => setStripeMode('live')}
                style={{
                  background: stripeMode === 'live' ? '#FFFFFF' : 'transparent',
                  color: stripeMode === 'live' ? '#1B4332' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '999px',
                  padding: '3px 10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Live
              </button>
            </div>

            <button
              onClick={handleCloseModal}
              style={{
                background: 'none',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '18px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginLeft: '6px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {checkoutComplete && completedReceipt ? (
          /* Post-Checkout Success Screen */
          <div style={{ padding: '36px 28px', textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#EBF5F1',
              color: '#1B4332',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              margin: '0 auto 16px',
            }}>
              ✓
            </div>
            <h3 style={{ fontSize: '22px', fontWeight: 800, color: '#1B4332', margin: '0 0 6px' }}>
              Subscription Confirmed!
            </h3>
            <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px', lineHeight: '1.5' }}>
              Your payment of <strong>{completedReceipt.amount}</strong> for <strong>{completedReceipt.planName}</strong> was successfully verified by Stripe.
            </p>

            <div style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #E2E8F0',
              textAlign: 'left',
              marginBottom: '24px',
              fontSize: '13px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748B' }}>Stripe Charge ID:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0F172A' }}>{completedReceipt.id}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748B' }}>Stripe Price ID:</span>
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#635BFF' }}>{matchedPrice}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748B' }}>Status:</span>
                <span style={{ color: '#10B981', fontWeight: 700 }}>Active (Auto-Renewing)</span>
              </div>
            </div>

            {/* Next Steps Guidance */}
            <div style={{
              backgroundColor: '#FFF8F5',
              borderRadius: '14px',
              padding: '18px',
              border: '1.5px solid #FCD4C2',
              textAlign: 'left',
              marginBottom: '24px',
            }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 800, color: '#A8442A' }}>
                📲 Next Steps: Connect Your App & Patients
              </h4>
              {plan.id.includes('practitioner') || plan.id.includes('clinic') ? (
                <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '12.5px', color: '#5C524C', lineHeight: '1.6' }}>
                  <li>Open your <strong>Clinician Studio</strong> in the web portal.</li>
                  <li>Configure custom CBT/DBT recipes or guardrail thresholds.</li>
                  <li>Click <strong>Pair Patient</strong> to generate a secure QR code or 6-digit PIN.</li>
                  <li>Have your client download the free <strong>Luminous Break</strong> app on iOS or Android and scan your code at <strong>$0 cost to the patient</strong>!</li>
                </ol>
              ) : (
                <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '12.5px', color: '#5C524C', lineHeight: '1.6' }}>
                  <li>Download the free <strong>Luminous Break</strong> companion app from the Apple App Store or Google Play Store.</li>
                  <li>Open the app and tap <strong>Sign In with Luminous Break Account</strong> using this billing email.</li>
                  <li>All 9 habit guardrail domains and sleep protection are immediately unlocked!</li>
                </ol>
              )}
            </div>

            <button
              onClick={handleCloseModal}
              style={{
                width: '100%',
                backgroundColor: '#26594A',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Continue to Portal
            </button>
          </div>
        ) : (
          <>
            {/* Plan Summary Banner */}
            <div style={{
              backgroundColor: '#F8FAFC',
              padding: '18px 24px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                  Subscribe to
                </span>
                <h3 style={{ margin: '2px 0 0', fontSize: '17px', fontWeight: 800, color: '#0F172A' }}>
                  {plan.name}
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748B' }}>
                  {plan.description}
                </p>
                <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#635BFF', marginTop: '4px', display: 'inline-block' }}>
                  Stripe ID: {matchedPrice}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '24px', fontWeight: 800, color: '#E07A5F' }}>
                  {plan.price}
                </span>
                <span style={{ fontSize: '12px', color: '#64748B', display: 'block' }}>
                  /{plan.billingInterval}
                </span>
              </div>
            </div>

            {/* Mock Payment Form */}
            <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
              <div style={{
                backgroundColor: stripeMode === 'live' ? '#ECFDF5' : '#EFF6FF',
                borderRadius: '10px',
                padding: '10px 14px',
                border: `1px solid ${stripeMode === 'live' ? '#A7F3D0' : '#BFDBFE'}`,
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span style={{ fontSize: '14px' }}>💳</span>
                <span style={{ fontSize: '11.5px', color: stripeMode === 'live' ? '#065F46' : '#1E40AF', lineHeight: '1.4' }}>
                  {stripeMode === 'live' ? (
                    <><strong>Live Stripe Production:</strong> Connected to account <code>acct_1UHtamJM5ejvHenB</code>. Valid payment method required.</>
                  ) : (
                    <><strong>Stripe Sandbox Active:</strong> Preloaded with valid test credentials. No real charges will be made.</>
                  )}
                </span>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '5px' }}>
                  Cardholder Full Name / Billing Entity
                </label>
                <input
                  type="text"
                  required
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    color: '#1E293B',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '5px' }}>
                  Card Information
                </label>
                <div style={{
                  display: 'flex',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                  <input
                    type="text"
                    required
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    placeholder="4242 4242 4242 4242"
                    style={{
                      flex: 2,
                      padding: '9px 12px',
                      border: 'none',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                  <input
                    type="text"
                    required
                    value={expDate}
                    onChange={(e) => setExpDate(e.target.value)}
                    placeholder="MM / YY"
                    style={{
                      flex: 1,
                      padding: '9px 12px',
                      borderLeft: '1px solid #CBD5E1',
                      borderRight: '1px solid #CBD5E1',
                      borderTop: 'none',
                      borderBottom: 'none',
                      fontSize: '13px',
                      outline: 'none',
                      textAlign: 'center',
                    }}
                  />
                  <input
                    type="text"
                    required
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value)}
                    placeholder="CVC"
                    style={{
                      flex: 0.8,
                      padding: '9px 12px',
                      border: 'none',
                      fontSize: '13px',
                      outline: 'none',
                      textAlign: 'center',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '5px' }}>
                  Postal / ZIP Code
                </label>
                <input
                  type="text"
                  required
                  value={billingZip}
                  onChange={(e) => setBillingZip(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    color: '#1E293B',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                style={{
                  width: '100%',
                  backgroundColor: stripeMode === 'live' ? '#1B4332' : '#635BFF',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '13px',
                  fontSize: '14.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(99,91,255,0.3)',
                  transition: 'all 0.15s ease',
                }}
              >
                {isProcessing
                  ? 'Verifying Token with Stripe...'
                  : `Pay ${plan.price} / ${plan.billingInterval} & Activate`}
              </button>

              <p style={{ textAlign: 'center', fontSize: '11px', color: '#94A3B8', marginTop: '12px', marginBottom: 0 }}>
                🔒 256-Bit SSL Encrypted • 30-Day Money-Back Guarantee • Instant 1-Click Cancellation
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default StripeMockCheckoutModal;
